import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Trash2, MessageSquare, X, Sparkles, Wand2, Upload, Loader2, Images } from 'lucide-react';
import { useApp } from '../context/AppContext';
import AppSidebar from '../components/AppSidebar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { api, fileUrl } from '../lib/api';

const kindOf = (img) => (img.edited ? 'edited' : img.generated ? 'generated' : 'uploaded');
const KIND_ICON = { generated: Sparkles, edited: Wand2, uploaded: Upload };

const download = async (img) => {
  const res = await fetch(fileUrl(img.id), { credentials: 'include' });
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = img.filename || 'aurra-image.png';
  a.click();
  URL.revokeObjectURL(a.href);
};

const ImageCard = ({ img, onOpen, onDelete, onDownload, onConv, t, lang }) => {
  const kind = kindOf(img);
  const Icon = KIND_ICON[kind];
  return (
    <div className="group relative rounded-2xl border border-border bg-card overflow-hidden animate-fade-in" data-testid={`gallery-item-${img.id}`}>
      <button onClick={onOpen} className="block w-full aspect-square bg-secondary" data-testid={`gallery-open-${img.id}`}>
        <img src={fileUrl(img.id)} alt={img.prompt || img.filename} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
      </button>
      <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-md bg-background/85 backdrop-blur px-2 py-1 text-[10px] font-semibold text-foreground" data-testid="gallery-kind-badge"><Icon className="h-3 w-3 text-primary" /> {t.gallery[kind]}</span>
      <div className="p-3">
        <p className="text-xs text-foreground line-clamp-2 min-h-[2rem]" title={img.prompt || img.filename}>{img.prompt || img.filename}</p>
        <p className="mt-1 text-[11px] text-muted-foreground">{new Date(img.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
        <div className="mt-2 flex items-center gap-1">
          <button onClick={onDownload} title={t.gallery.download} data-testid={`gallery-download-${img.id}`} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground"><Download className="h-4 w-4" /></button>
          {img.conversation_id && <button onClick={onConv} title={t.gallery.openConv} data-testid={`gallery-conv-${img.id}`} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground"><MessageSquare className="h-4 w-4" /></button>}
          <button onClick={onDelete} title={t.gallery.delete} data-testid={`gallery-delete-${img.id}`} className="ml-auto h-8 w-8 rounded-lg hover:bg-destructive/10 flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

const Lightbox = ({ img, onClose, onDelete, onDownload, onConv, t }) => (
  <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose} data-testid="gallery-lightbox">
    <button onClick={onClose} className="absolute top-4 right-4 h-10 w-10 rounded-xl bg-card border border-border flex items-center justify-center hover:bg-accent" data-testid="gallery-lightbox-close"><X className="h-5 w-5" /></button>
    <div className="max-w-4xl w-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
      <img src={fileUrl(img.id)} alt={img.prompt || img.filename} className="max-h-[70vh] w-auto max-w-full rounded-2xl border border-border shadow-2xl" />
      {img.prompt && <p className="text-sm text-center text-muted-foreground max-w-2xl" data-testid="gallery-lightbox-prompt">{img.prompt}</p>}
      <div className="flex flex-wrap justify-center gap-2">
        <Button onClick={onDownload} variant="outline" className="rounded-xl font-semibold gap-2" data-testid="gallery-lightbox-download"><Download className="h-4 w-4" /> {t.gallery.download}</Button>
        {img.conversation_id && <Button onClick={onConv} variant="outline" className="rounded-xl font-semibold gap-2" data-testid="gallery-lightbox-conv"><MessageSquare className="h-4 w-4" /> {t.gallery.openConv}</Button>}
        <Button onClick={onDelete} variant="outline" className="rounded-xl font-semibold gap-2 text-destructive hover:text-destructive" data-testid="gallery-lightbox-delete"><Trash2 className="h-4 w-4" /> {t.gallery.delete}</Button>
      </div>
    </div>
  </div>
);

const Gallery = () => {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.listImages().then(setImages).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => (filter === 'all' ? images : images.filter((i) => kindOf(i) === filter)), [images, filter]);

  const remove = async (img) => {
    if (!window.confirm(t.gallery.confirm)) return;
    try {
      await api.deleteImage(img.id);
      setImages((prev) => prev.filter((i) => i.id !== img.id));
      if (open?.id === img.id) setOpen(null);
      toast.success(t.gallery.deleted);
    } catch (e) { toast.error(e.message); }
  };
  const dl = (img) => download(img).catch(() => toast.error(lang === 'fr' ? 'Téléchargement impossible' : 'Download failed'));
  const goConv = (img) => navigate(`/chat/${img.conversation_id}`);

  return (
    <div className="flex bg-background" data-testid="gallery-page">
      <AppSidebar conversations={[]} onNewChat={() => navigate('/chat')} />
      <main className="flex-1 h-screen overflow-y-auto min-w-0">
        <div className="h-16 border-b border-border flex items-center justify-between px-5 sm:px-8 sticky top-0 bg-background/80 backdrop-blur z-10">
          <h1 className="font-display text-xl font-bold flex items-center gap-2"><Images className="h-5 w-5 text-primary" /> {t.gallery.title}</h1>
          <span className="text-xs font-medium text-muted-foreground" data-testid="gallery-count">{t.gallery.count.replace('{n}', shown.length)}</span>
        </div>

        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
          <p className="text-muted-foreground">{t.gallery.subtitle}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {['all', 'generated', 'edited', 'uploaded'].map((k) => (
              <button key={k} data-testid={`gallery-filter-${k}`} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === k ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>{t.gallery[k]}</button>
            ))}
          </div>

          {loading ? (
            <div className="mt-8 flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : shown.length === 0 ? (
            <div className="mt-8 rounded-3xl border border-dashed border-border py-20 text-center" data-testid="gallery-empty">
              <span className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center"><Images className="h-7 w-7 text-primary" /></span>
              <h2 className="mt-5 font-display text-lg font-bold">{t.gallery.empty}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t.gallery.emptySub}</p>
              <Button onClick={() => navigate('/chat')} className="mt-6 rounded-xl font-semibold" data-testid="gallery-go-chat">{t.gallery.goChat}</Button>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
              {shown.map((img) => <ImageCard key={img.id} img={img} t={t} lang={lang} onOpen={() => setOpen(img)} onDelete={() => remove(img)} onDownload={() => dl(img)} onConv={() => goConv(img)} />)}
            </div>
          )}
        </div>
      </main>
      {open && <Lightbox img={open} t={t} onClose={() => setOpen(null)} onDelete={() => remove(open)} onDownload={() => dl(open)} onConv={() => goConv(open)} />}
    </div>
  );
};

export default Gallery;
